'use client';

import { createContext, useState } from 'react';

import type { PropsWithChildren } from 'react';
import type { Or2Analytics } from '@ortoo/analytics';
import Script from 'next/script';

import { env } from '@/env';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

interface Or2AnalyticsValue {
  analytics: Or2Analytics | null;
  refreshAnalytics: () => Promise<Or2Analytics | void>;
}

export const Or2AnalyticsContext = createContext<Or2AnalyticsValue>({
  analytics: null,
  refreshAnalytics: async () => undefined,
});

export const Or2AnalyticsProvider = ({ children }: PropsWithChildren) => {
  const [analytics, setAnalytics] = useState<Or2Analytics | null>(null);

  const refreshAnalytics = async () => {
    const { Or2Analytics } = await import('@ortoo/analytics');

    const newAnalytics = new Or2Analytics();

    setAnalytics(newAnalytics);

    return newAnalytics;
  };

  if (env.NEXT_PUBLIC_NODE_ENV === 'development') {
    return (
      <Or2AnalyticsContext.Provider value={{ analytics, refreshAnalytics }}>
        {children}
      </Or2AnalyticsContext.Provider>
    );
  }

  return (
    <>
      {/*
        Inspired by what you find in the source code for @next/third-parties
        Source: https://github.com/vercel/next.js/blob/canary/packages/third-parties/src/google/ga.tsx
      */}
      <Script
        id="google-analytics-init"
        dangerouslySetInnerHTML={{
          __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', '${env.NEXT_PUBLIC_GA_TRACKING_ID}');`,
        }}
      />
      <Script
        id="google-analytics"
        src={`https://www.googletagmanager.com/gtag/js?id=${env.NEXT_PUBLIC_GA_TRACKING_ID}`}
        onLoad={async () => {
          const newAnalytics = await refreshAnalytics();

          // This marks the initial page load
          newAnalytics.page();
        }}
      />
      <Or2AnalyticsContext.Provider value={{ analytics, refreshAnalytics }}>
        {children}
      </Or2AnalyticsContext.Provider>
    </>
  );
};
